import type { Router } from 'vue-router'

const prefetchedRouteNames = new Set<string>()

type LazyRouteComponent = () => Promise<unknown>

function isLazyRouteComponent(component: unknown): component is LazyRouteComponent {
  return typeof component === 'function'
}

/**
 * Warm the async chunk(s) for a named route so the first click feels faster.
 * Safe to call on hover/focus; failures clear the cache entry so a later click can retry.
 */
export function prefetchNamedRoute(router: Router, routeName: string | undefined | null) {
  if (!routeName || prefetchedRouteNames.has(routeName)) {
    return
  }

  prefetchedRouteNames.add(routeName)

  try {
    const resolved = router.resolve({ name: routeName })
    for (const record of resolved.matched) {
      for (const component of Object.values(record.components ?? {})) {
        if (!isLazyRouteComponent(component)) {
          continue
        }
        const loadResult = component()
        if (loadResult && typeof (loadResult as Promise<unknown>).then === 'function') {
          void (loadResult as Promise<unknown>).catch(() => {
            prefetchedRouteNames.delete(routeName)
          })
        }
      }
    }
  } catch {
    prefetchedRouteNames.delete(routeName)
  }
}
